import { useState, useEffect, FC, PropsWithChildren, useRef } from 'react'
import { useLocation } from 'react-router'
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar'
import { Navigate } from 'react-router'
import { useAuth } from 'hooks/useAuth'
import { User, getUserCache } from 'gql/hooks/useUser'
import { startPdfDownload } from '~/lib/serviceWorker'
import { env } from '~/env'

interface ProtectedRouteProps extends PropsWithChildren {
  admin?: boolean
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, admin = false }) => {
  const topLoaderRef = useRef<LoadingBarRef>(null)
  const { pathname } = useLocation()
  const { getUser, getToken } = useAuth()

  const token = getToken()
  const cached = getUserCache()

  const [user, setUser] = useState<User | null>(cached)
  const [isLoading, setIsLoading] = useState<boolean>(!cached && !!token)

  useEffect(() => {
    // No token → redirect handled at render time
    if (!token) return

    // Valid cache available — skip network call, start PDF service
    if (cached) {
      startPdfDownload(token, env.VITE_API_ENDPOINT)
      return
    }

    // No cache — validate with backend (first load or after cache expiry)
    topLoaderRef.current?.continuousStart()
    const runAsyncEffect = async () => {
      try {
        const validatedUser = await getUser()
        if (validatedUser?.me) {
          setUser(validatedUser.me)
          startPdfDownload(getToken() ?? '', env.VITE_API_ENDPOINT)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
        topLoaderRef.current?.complete()
      }
    }
    void runAsyncEffect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // No token → redirect immediately without any loading state
  if (!token) {
    if (pathname.includes('procore')) return <Navigate to="/procore/login" replace />
    return <Navigate to="/login" replace />
  }

  if (!isLoading && !user?.email) {
    if (pathname.includes('procore')) return <Navigate to="/procore/login" replace />
    return <Navigate to="/login" replace />
  }

  if (!isLoading && admin && !user?.isAdmin) return <Navigate to="/org" replace />

  return (
    <>
      <LoadingBar
        color="#e44386"
        ref={topLoaderRef}
        transitionTime={100}
        loaderSpeed={200}
        waitingTime={400}
      />
      {!isLoading && children}
    </>
  )
}

export default ProtectedRoute
